// «Οι στόχοι της ημέρας» (handoff v08 #22) — the adaptive daily goals card, shown in «Η μέρα μου» and next to the
// hour-by-hour planner (Agenda → Day). The numbers are computed locally (packages/domain/src/daily-goals.ts): no AI, no
// invented market news; each goal says why, can be changed, skipped for the day or switched off; progress is counted
// from recorded activity only.
import { useEffect, useMemo, useState } from 'react';
import {
  adjustDailyGoal, dailyGoals, ensureDailyGoals, goalPrefs, goalProgress, localDayOf, recomputeDailyGoals, relationshipCounts, saveSetting, setDailyGoalStatus, setMarketNote,
  workingWindow, DAILY_GOAL_TYPES, GOAL_LIMITS, GOAL_PREFS_KEY, type DailyGoal, type DailyGoalType, type GoalInputs, type GoalPrefs, type GoalReason,
} from '@vero/domain';
import type { I18n } from '@vero/i18n';
import { useRuntime } from '../core/runtime.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate } from '../core/router.ts';

type Ev = { target: { value: string; checked: boolean } };
const hoursText = (i18n: I18n, min: number) => i18n.number(Math.round((min / 60) * 10) / 10);
/** a reason in words; the numbers are formatted for the locale */
export function goalReasonText(i18n: I18n, r: GoalReason): string {
  const p: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(r.params)) p[k] = typeof v === 'number' ? i18n.number(v) : v;
  return i18n.t(`goals.why.${r.key}`, p);
}

export function DailyGoalsCard({ day, compact }: { day: string; compact?: boolean }) {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const snap = dailyGoals(view, day);
  const [busy, setBusy] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [marketOpen, setMarketOpen] = useState(false);
  // the first time a working day is opened its goals are computed and stored once (the snapshot of that morning).
  // Owner 22 Oct 2026: a later date chosen in the planner (compact card) gets its goals from THAT day's free hours; when
  // that day comes, goals computed on an earlier day are computed again once (the advisor's own numbers stay).
  useEffect(() => {
    if (rt.ws.readOnlyReason) return;
    if (!snap && (day === today || (compact && day > today))) { void rt.ws.run((v, c) => ensureDailyGoals(v, day, c)).catch(() => undefined); return; }
    if (snap && day === today && (localDayOf(snap.computedAt) ?? day) < day) void rt.ws.run((v, c) => recomputeDailyGoals(v, day, c)).catch(() => undefined);
  }, [snap, day, today, compact]);
  const rel = useMemo(() => relationshipCounts(view, day), [view, day]);
  const progress = useMemo(() => (snap ? goalProgress(view, snap) : []), [view, snap]);
  const inputs = (snap?.inputs ?? null) as GoalInputs | null;
  const compute = async (force: boolean) => { setBusy(true); await rt.run((v, c) => (snap ? recomputeDailyGoals(v, day, c) : ensureDailyGoals(v, day, c, { force })), snap ? 'goals.recomputed' : undefined); setBusy(false); };

  return (
    <section className="card goals-card" data-testid="daily-goals">
      <div className="card-head">
        <h3><Icon name="spark" />{day === today ? t('goals.title') : t('goals.title_for', { date: i18n.date(day, 'dayMonth') })}</h3>
        <div className="row" style={{ gap: 4 }}>
          {snap && <button className="btn btn-sm btn-ghost" onClick={() => void compute(true)} disabled={busy} data-testid="goals-recompute" title={t('goals.recompute_hint')}>{t('goals.recompute')}</button>}
          <button className="btn btn-sm btn-ghost" onClick={() => setPrefsOpen((x) => !x)} aria-expanded={prefsOpen} data-testid="goals-prefs"><Icon name="settings" />{t('goals.prefs')}</button>
          {!compact && <button className="btn btn-sm" onClick={() => navigate(`/agenda?day=${day}`)} data-testid="goals-plan-day"><Icon name="agenda" />{t('goals.plan_day')}</button>}
        </div>
      </div>
      <div className="goals-body">
        <p className="tiny muted" style={{ margin: '0 0 8px' }}>{t('goals.subtitle')}</p>
        {prefsOpen && <GoalPrefsPanel onDone={() => setPrefsOpen(false)} />}
        {!snap ? (
          !workingWindow(view, day) ? <DayOff day={day} onForce={() => void compute(true)} />
            : <div className="row wrap" style={{ gap: 8 }}><span className="small muted">{t('goals.not_computed')}</span><button className="btn btn-sm" onClick={() => void compute(true)} data-testid="goals-compute">{t('goals.compute')}</button></div>
        ) : (
          <>
            {inputs && inputs.workMin > 0 && <p className="small" style={{ margin: '0 0 10px' }} data-testid="goals-free">{t('goals.free_line', { free: hoursText(i18n, inputs.freeMin), work: hoursText(i18n, inputs.workMin), from: inputs.from ?? '', to: inputs.to ?? '' })}</p>}
            {snap.goals.length === 0 && <p className="small muted" data-testid="goals-none">{t('goals.none')}</p>}
            <div className="goals-list">
              {snap.goals.map((g) => <GoalRow key={g.type} day={day} goal={g} done={progress.find((p) => p.type === g.type)?.done ?? 0} compact={compact} />)}
            </div>
            {snap.goals.length > 0 && <p className="tiny muted" style={{ margin: '8px 0 0' }}>{t('goals.counted')}</p>}
            {rel.total > 0 && <p className="small goal-relationship" data-testid="goals-relationship">{t('goals.relationship', { planned: i18n.number(rel.planned), done: i18n.number(rel.done) })} <span className="tiny muted">{t('goals.relationship_hint')}</span></p>}
            <Market day={day} open={marketOpen} setOpen={setMarketOpen} />
          </>
        )}
      </div>
    </section>
  );
}

function DayOff({ day, onForce }: { day: string; onForce: () => void }) {
  const { t, i18n } = useRuntime();
  return <div className="row wrap" style={{ gap: 8 }} data-testid="goals-day-off"><span className="small muted">{t('goals.none_day_off', { date: i18n.date(day, 'long') })}</span><button className="btn btn-sm" onClick={onForce} data-testid="goals-force">{t('goals.compute_anyway')}</button></div>;
}

function GoalRow({ day, goal, done, compact }: { day: string; goal: DailyGoal; done: number; compact?: boolean }) {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const skipped = goal.status === 'skipped';
  const pct = goal.target > 0 ? Math.min(100, Math.round((done / goal.target) * 100)) : 0;
  const set = (n: number) => void rt.run((v, c) => adjustDailyGoal(v, day, goal.type, Math.max(0, Math.min(GOAL_LIMITS[goal.type], n)), c));
  return (
    <article className={`goal-row${skipped ? ' skipped' : ''}${!skipped && done >= goal.target ? ' reached' : ''}`} data-testid="goal-row" data-type={goal.type}>
      <div className="goal-top">
        <b className="goal-label">{t(`goals.type.${goal.type}`)}</b>
        <span className="goal-count num" data-testid="goal-progress">{skipped ? t('goals.skipped') : t('goals.progress', { done: i18n.number(done), target: i18n.number(goal.target) })}</span>
      </div>
      {!skipped && <div className="goal-bar" role="progressbar" aria-valuemin={0} aria-valuemax={goal.target} aria-valuenow={done} aria-label={t(`goals.type.${goal.type}`)}><span style={{ width: `${pct}%` }} /></div>}
      {!compact || !skipped ? <ul className="goal-why">{goal.reasons.map((r, i) => <li key={i}>{goalReasonText(i18n, r)}</li>)}{goal.adjusted && <li className="adjusted">{t('goals.adjusted', { n: i18n.number(goal.suggested) })}</li>}</ul> : null}
      <div className="row wrap goal-actions" style={{ gap: 4 }}>
        {!skipped && <>
          <button className="btn btn-sm btn-icon" aria-label={t('goals.decrease')} onClick={() => set(goal.target - 1)} disabled={goal.target <= 0} data-testid="goal-minus">−</button>
          <input className="input goal-input" type="number" min={0} max={GOAL_LIMITS[goal.type]} value={goal.target} aria-label={t('goals.target_for', { goal: t(`goals.type.${goal.type}`) })} onChange={(e: Ev) => { const n = Number(e.target.value); if (Number.isFinite(n) && e.target.value !== '') set(n); }} data-testid="goal-input" />
          <button className="btn btn-sm btn-icon" aria-label={t('goals.increase')} onClick={() => set(goal.target + 1)} data-testid="goal-plus">+</button>
        </>}
        <button className="btn btn-sm btn-ghost" onClick={() => void rt.run((v, c) => setDailyGoalStatus(v, day, goal.type, skipped ? 'active' : 'skipped', c))} data-testid="goal-skip">{skipped ? t('goals.resume') : t('goals.skip')}</button>
      </div>
    </article>
  );
}

function Market({ day, open, setOpen }: { day: string; open: boolean; setOpen: (b: boolean) => void }) {
  const rt = useRuntime();
  const { t, view } = rt;
  const m = dailyGoals(view, day)?.market ?? null;
  const [f, setF] = useState({ text: '', source: '', url: '' });
  if (m) return (
    <div className="goal-market" data-testid="goals-market">
      <span className="upper tiny">{t('goals.market')}</span>
      <p className="small" style={{ margin: '2px 0' }}>{m.text} — {m.url ? <a href={m.url} target="_blank" rel="noreferrer">{m.source}</a> : m.source}</p>
      <button className="btn btn-sm btn-ghost" onClick={() => void rt.run((v, c) => setMarketNote(v, day, null, c))}>{t('goals.market_remove')}</button>
    </div>
  );
  if (!open) return <button className="btn btn-sm btn-ghost" style={{ marginTop: 6 }} onClick={() => setOpen(true)} data-testid="goals-market-add">{t('goals.market_add')}</button>;
  return (
    <div className="goal-market stack" style={{ gap: 6 }}>
      <p className="tiny muted" style={{ margin: 0 }}>{t('goals.market_hint')}</p>
      <input className="input" placeholder={t('goals.market_text')} aria-label={t('goals.market_text')} value={f.text} onChange={(e: Ev) => setF({ ...f, text: e.target.value })} maxLength={300} />
      <input className="input" placeholder={t('goals.market_source')} aria-label={t('goals.market_source')} value={f.source} onChange={(e: Ev) => setF({ ...f, source: e.target.value })} maxLength={200} />
      <input className="input" placeholder={t('goals.market_url')} aria-label={t('goals.market_url')} value={f.url} onChange={(e: Ev) => setF({ ...f, url: e.target.value })} />
      <div className="row" style={{ gap: 6 }}>
        <button className="btn btn-sm btn-primary" disabled={!f.text.trim() || !f.source.trim()} onClick={async () => { if (await rt.run((v, c) => setMarketNote(v, day, f, c), 'goals.saved')) setOpen(false); }}>{t('common.save')}</button>
        <button className="btn btn-sm btn-ghost" onClick={() => setOpen(false)}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}

function GoalPrefsPanel({ onDone }: { onDone: () => void }) {
  const rt = useRuntime();
  const { t } = rt;
  const [p, setP] = useState<GoalPrefs>(() => goalPrefs(rt.view));
  const num = (x: string) => (x === '' ? undefined : Math.max(0, Math.round(Number(x))));
  const save = async () => { if (await rt.run((v, c) => saveSetting(v, GOAL_PREFS_KEY, p, c), 'goals.prefs_saved')) onDone(); };
  return (
    <div className="goal-prefs" data-testid="goals-prefs-panel">
      <table className="table compact"><thead><tr><th>{t('goals.pref_goal')}</th><th>{t('goals.pref_on')}</th><th>{t('goals.pref_min')}</th><th>{t('goals.pref_max')}</th></tr></thead>
        <tbody>{DAILY_GOAL_TYPES.map((ty: DailyGoalType) => (
          <tr key={ty}>
            <td>{t(`goals.type.${ty}`)}</td>
            <td><input type="checkbox" checked={p.enabled[ty]} onChange={(e: Ev) => setP({ ...p, enabled: { ...p.enabled, [ty]: e.target.checked } })} aria-label={`${t('goals.pref_on')} · ${t(`goals.type.${ty}`)}`} data-testid={`goal-pref-on-${ty}`} /></td>
            <td><input className="input goal-input" type="number" min={0} max={GOAL_LIMITS[ty]} value={p.min[ty] ?? ''} onChange={(e: Ev) => setP({ ...p, min: { ...p.min, [ty]: num(e.target.value) } })} aria-label={`${t('goals.pref_min')} · ${t(`goals.type.${ty}`)}`} /></td>
            <td><input className="input goal-input" type="number" min={0} max={GOAL_LIMITS[ty]} value={p.max[ty] ?? ''} onChange={(e: Ev) => setP({ ...p, max: { ...p.max, [ty]: num(e.target.value) } })} aria-label={`${t('goals.pref_max')} · ${t(`goals.type.${ty}`)}`} /></td>
          </tr>
        ))}</tbody></table>
      <label className="row small" style={{ gap: 8, marginTop: 8 }}>{t('goals.pref_util')}
        <select className="input" style={{ width: 'auto' }} value={String(p.utilisation)} onChange={(e: Ev) => setP({ ...p, utilisation: Number(e.target.value) })}>
          {[0.4, 0.5, 0.6, 0.7, 0.8].map((u) => <option key={u} value={String(u)}>{Math.round(u * 100)}%</option>)}
        </select>
      </label>
      <p className="tiny muted">{t('goals.prefs_note')}</p>
      <div className="row" style={{ gap: 6 }}><button className="btn btn-sm btn-primary" onClick={() => void save()} data-testid="goals-prefs-save">{t('common.save')}</button><button className="btn btn-sm btn-ghost" onClick={onDone}>{t('common.cancel')}</button></div>
    </div>
  );
}
